/**
 * invoice-info.ts — Form "Thông tin xuất hoá đơn" (hàm thuần): nháp ↔ payload + kiểm tra trước
 * "Đặt hàng". Cùng quy tắc với server (ecdp_backend_source/src/modules/billing/einvoice/invoice-info.ts);
 * server vẫn kiểm tra lại.
 */
import type { BuyerType, InvoiceInfo, InvoiceProfile } from '@/hooks/use-billing'
import { i18n } from '@/lib/i18n'

export interface InvoiceDraft {
  buyerType: BuyerType
  companyName: string
  taxCode: string
  buyerName: string
  nationalId: string
  address: string
  /** Nhiều email cách nhau bằng dấu phẩy. */
  emails: string
  phone: string
  bankAccount: string
  bankName: string
}

export type InvoiceField = keyof InvoiceDraft
export type InvoiceErrors = Partial<Record<InvoiceField, string>>

export const MAX_INVOICE_EMAILS = 5
const COMPANY_TAX_CODE_RE = /^\d{10}(-\d{3})?$/
const NATIONAL_ID_RE = /^\d{12}$/
const EMAIL_RE = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]{2,}$/
const PHONE_RE = /^\+?[0-9][0-9 .-]{6,18}[0-9]$/

export function emptyDraft(buyerType: BuyerType = 'company', email = ''): InvoiceDraft {
  return { buyerType, companyName: '', taxCode: '', buyerName: '', nationalId: '', address: '', emails: email, phone: '', bankAccount: '', bankName: '' }
}

export function draftFromInfo(i: InvoiceInfo): InvoiceDraft {
  return {
    buyerType: i.buyerType,
    companyName: i.companyName ?? '',
    taxCode: i.taxCode ?? '',
    buyerName: i.buyerName ?? '',
    nationalId: i.nationalId ?? '',
    address: i.address ?? '',
    emails: i.emails.join(', '),
    phone: i.phone ?? '',
    bankAccount: i.bankAccount ?? '',
    bankName: i.bankName ?? '',
  }
}

export function normalizeTaxCode(raw: string): string {
  return raw.replace(/[\s.]/g, '').replace(/[–—]/g, '-')
}

export function isValidTaxCode(raw: string, buyerType: BuyerType = 'company'): boolean {
  const v = normalizeTaxCode(raw)
  return COMPANY_TAX_CODE_RE.test(v) || (buyerType === 'individual' && NATIONAL_ID_RE.test(v))
}

export function splitEmails(raw: string): string[] {
  const out: string[] = []
  for (const e of raw.split(/[,;\s]+/)) {
    const t = e.trim().toLowerCase()
    if (t && !out.includes(t)) out.push(t)
  }
  return out
}

/** Kiểm tra nháp → lỗi theo trường (rỗng = hợp lệ). */
export function validateDraft(d: InvoiceDraft): InvoiceErrors {
  const e: InvoiceErrors = {}
  const company = d.buyerType === 'company'
  if (company && !d.companyName.trim()) e.companyName = i18n.t('billing:invoiceInfo.enterCompanyName')
  if (!company && !d.buyerName.trim()) e.buyerName = i18n.t('billing:invoiceInfo.enterBuyerName')
  const tax = normalizeTaxCode(d.taxCode)
  if (company && !tax) e.taxCode = i18n.t('billing:invoiceInfo.enterTaxCode')
  else if (company && !isValidTaxCode(tax, d.buyerType)) e.taxCode = i18n.t('billing:invoiceInfo.invalidTaxCode')
  if (!company && d.nationalId.trim() && !NATIONAL_ID_RE.test(d.nationalId.replace(/\s/g, ''))) e.nationalId = i18n.t('billing:invoiceInfo.invalidNationalId')
  if (!d.address.trim()) e.address = i18n.t('billing:invoiceInfo.enterAddress')
  const emails = splitEmails(d.emails)
  if (emails.length === 0) e.emails = i18n.t('billing:invoiceInfo.enterAtLeastOneEmail')
  else if (emails.length > MAX_INVOICE_EMAILS) e.emails = i18n.t('billing:invoiceInfo.maxEmails', { max: MAX_INVOICE_EMAILS })
  else {
    const bad = emails.find((x) => !EMAIL_RE.test(x))
    if (bad) e.emails = i18n.t('billing:invoiceInfo.invalidEmail', { email: bad })
  }
  if (d.phone.trim() && !PHONE_RE.test(d.phone.trim())) e.phone = i18n.t('billing:invoiceInfo.invalidPhone')
  return e
}

/** Nháp → body gửi server (bỏ trường không thuộc loại người mua). */
export function draftToPayload(d: InvoiceDraft): InvoiceInfo {
  const company = d.buyerType === 'company'
  const t = (s: string) => s.trim().replace(/\s+/g, ' ') || null
  return {
    buyerType: d.buyerType,
    companyName: company ? t(d.companyName) : null,
    taxCode: company ? normalizeTaxCode(d.taxCode) || null : null,
    buyerName: t(d.buyerName),
    nationalId: company ? null : d.nationalId.replace(/\s/g, '') || null,
    address: t(d.address),
    emails: splitEmails(d.emails),
    phone: t(d.phone),
    bankAccount: null,
    bankName: null,
  }
}

export function profileLabel(p: Pick<InvoiceProfile, 'buyerType' | 'companyName' | 'buyerName' | 'taxCode'>): string {
  const name = p.buyerType === 'company' ? p.companyName : p.buyerName
  return [name || '—', p.buyerType === 'company' && p.taxCode ? i18n.t('billing:invoiceInfo.taxCodeLabel', { code: p.taxCode }) : p.buyerType === 'company' ? null : i18n.t('billing:invoiceInfo.individualOrHousehold')].filter(Boolean).join(' · ')
}

/** Trạng thái chọn hoá đơn trên trang "Thay đổi gói". */
export interface InvoiceChoice {
  enabled: boolean
  /** Mục danh bạ đang chọn; null = nhập mới. */
  profileId: string | null
  /** Đang sửa mục đã chọn (gửi kèm trường; lưu = cập nhật mục đó). */
  editing: boolean
  draft: InvoiceDraft
  /** Lưu vào danh bạ (mặc định bật). */
  save: boolean
}

/**
 * Body `invoice` cho POST /billing/checkout (hoặc /orders/:id/invoice).
 *   - tắt → null
 *   - chọn mục danh bạ → { profileId }
 *   - sửa mục đã chọn → { ...trường, profileId, save }
 *   - nhập mới → { ...trường, save }
 * Trả { error } khi nháp chưa hợp lệ.
 */
export function buildInvoiceBody(c: InvoiceChoice): { body: Record<string, unknown> | null; errors: InvoiceErrors } {
  if (!c.enabled) return { body: null, errors: {} }
  if (c.profileId && !c.editing) return { body: { profileId: c.profileId }, errors: {} }
  const errors = validateDraft(c.draft)
  if (Object.keys(errors).length) return { body: null, errors }
  return { body: { ...draftToPayload(c.draft), ...(c.profileId ? { profileId: c.profileId } : {}), save: c.save }, errors: {} }
}

export const INVOICE_STATUS: Record<string, { text: string; variant: 'success' | 'warning' | 'destructive' | 'secondary' }> = {
  get none() { return { text: i18n.t('billing:invoiceInfo.status.none'), variant: 'secondary' as const } },
  get pending() { return { text: i18n.t('billing:invoiceInfo.status.pending'), variant: 'warning' as const } },
  get issued() { return { text: i18n.t('billing:invoiceInfo.status.issued'), variant: 'success' as const } },
  get failed() { return { text: i18n.t('billing:invoiceInfo.status.failed'), variant: 'destructive' as const } },
  get cancelled() { return { text: i18n.t('billing:invoiceInfo.status.cancelled'), variant: 'secondary' as const } },
}

/** Dòng tóm tắt hoá đơn ở khung xem trước đơn hàng. */
export function invoiceSummary(c: InvoiceChoice | null, profiles: { id: string; buyerType: 'company' | 'individual'; companyName: string | null; buyerName: string | null; taxCode: string | null }[]): string {
  if (!c?.enabled) return i18n.t('billing:invoiceInfo.status.none')
  const p = !c.editing && c.profileId ? profiles.find((x) => x.id === c.profileId) : null
  if (p) return profileLabel(p)
  const d = c.draft
  const name = (d.buyerType === 'company' ? d.companyName : d.buyerName).trim()
  return name ? profileLabel({ buyerType: d.buyerType, companyName: d.companyName.trim(), buyerName: d.buyerName.trim(), taxCode: d.taxCode.trim() || null }) : i18n.t('billing:invoiceInfo.noInfoEntered')
}
